import { SCENES, type Scene } from '@obv/core';
import { useLocation } from 'wouter';
import { useUpdatePreferences } from '@/queries/hooks';
import { useDisplay } from '../context';
import { SceneIcon } from '../icons';
import { Pill, ScreenHeader, Toggle } from '../ui';

export function ScenesScreen({ active }: { active: Scene }) {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const prefs = useUpdatePreferences();
  const choose = (scene: Scene) => {
    prefs.mutate({ scene });
    navigate('/display');
  };
  return (
    <>
      <ScreenHeader eyebrow="Make space for your routine" title="Home, in your rhythm." right={<Pill>One scene at a time</Pill>} />
      <div
        className="mt-4 grid min-h-0 flex-1"
        style={{
          gridTemplateColumns: ctx.orientation === 'portrait' ? 'repeat(2, minmax(0,1fr))' : 'repeat(3, minmax(0,1fr))',
          gridTemplateRows: ctx.orientation === 'portrait' ? 'repeat(3, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
          gridGap: 14,
        }}
      >
        {SCENES.map((sc) => {
          const on = sc.key === active;
          return (
            <button
              key={sc.key}
              type="button"
              aria-pressed={on}
              onClick={() => choose(sc.key)}
              className="tap card flex flex-col px-6 py-[22px] text-left"
              style={{
                background: on ? 'var(--ink)' : `var(--sc${sc.key}, var(--card))`,
                color: on ? 'var(--onInk)' : 'var(--ink)',
                borderColor: on ? 'var(--ink)' : 'var(--line)',
              }}
            >
              <span className="flex w-full items-start justify-between">
                <SceneIcon icon={sc.icon} />
                <span
                  className="rounded-full border px-[11px] py-[6px] text-[12px] font-medium"
                  style={{ borderColor: on ? 'var(--onInkSub)' : 'var(--bd)', color: on ? 'var(--onInkSub)' : 'var(--muted)' }}
                >
                  {on ? 'Active' : 'Tap to use'}
                </span>
              </span>
              <span className="mt-auto block text-[22px]">{sc.title}</span>
              <span className="mt-[6px] block text-[14px] leading-[1.4]" style={{ color: on ? 'var(--onInkSub)' : 'var(--muted)' }}>
                {sc.desc}
              </span>
              <span className="mt-3 block text-[12px] tracking-[0.04em]" style={{ color: on ? 'var(--onInkSub)' : 'var(--muted)' }}>
                {sc.shows}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-[14px] flex flex-none items-center rounded-[14px] px-4" style={{ background: 'var(--soft)', boxShadow: 'inset 0 0 0 1px var(--softBd)' }}>
        <div className="min-w-0 flex-1 py-3 text-[13px]">
          <b className="font-semibold">Your home, your pace.</b>{' '}
          <span className="text-muted2">
            Scenes change this display only. Morning starts {ctx.prefs.morning_starts}, evening {ctx.prefs.evening_starts}.
          </span>
        </div>
        <div className="ml-4 w-[230px] flex-none [&>button]:border-t-0">
          <Toggle label="Follow schedule" on={ctx.prefs.follow_schedule} onChange={(v) => prefs.mutate({ follow_schedule: v })} />
        </div>
      </div>
    </>
  );
}
